/**
 * MGP FEP - 도메인 설정 유틸리티
 * 
 * 환경변수와 games.json을 기반으로 도메인 관련 설정을 제공합니다.
 * GTM을 통해 모든 추적 코드(GA4, Meta Pixel 등)를 관리합니다.
 */

import gamesConfig from '../config/games.json';

// 게임별 도메인 환경변수 매핑 함수 (export로 일원화)
export const getGameDomain = (gameSvcCd: string): string | undefined => {
  const code = (gameSvcCd || '').toLowerCase();
  switch (code) {
    case 'brbr':
      return process.env.REACT_APP_DOMAIN_BEARBRICK || '';
    case 'bbo':
      return process.env.REACT_APP_DOMAIN_BEARBRICK || '';
    case 'dgmn':
      return process.env.REACT_APP_DOMAIN_DIGIMON || '';
    case 'dgrp':
      return process.env.REACT_APP_DOMAIN_DGMRPG || '';
    default:
      return undefined;
  }
};

// gamesConfig.games에 도메인 주입
export const gamesWithDomain = gamesConfig.games.map(game => ({
  ...game,
  domain: getGameDomain(game.code) || ''
}));

// 환경변수 설정
export const config = {
  mainDomain: process.env.REACT_APP_MAIN_DOMAIN || 'mgp.gameking.com',
  baseDomain: process.env.REACT_APP_BASE_DOMAIN || 'gameking.com',
  mainPort: process.env.REACT_APP_MAIN_PORT || '',
  gtmId: process.env.REACT_APP_GTM_ID || 'GTM-XXXXXXX',
};

/**
 * 도메인 → gameSvcCd 매핑 생성
 * 
 * @returns Record<string, string> - { 'bearbrick.gameking.com': 'brbr', ... }
 */
export const getDomainGameMap = (): Record<string, string> => {
  const map: Record<string, string> = {};
  gamesConfig.games.forEach(game => {
    const domain = getGameDomain(game.code);
    if (domain) map[domain] = game.code;
  });
  return map;
};

/**
 * gameSvcCd → 도메인 매핑 생성
 * 
 * @returns Record<string, string> - { 'brbr': 'bearbrick.gameking.com', ... }
 */
// getGameDomain은 위에서 export로 일원화

/**
 * 현재 도메인이 서브도메인인지 확인
 * 
 * @returns boolean - 서브도메인이면 true
 */
export const isSubdomain = (): boolean => {
  const hostname = window.location.hostname;
  if (!config.baseDomain) return hostname !== config.mainDomain;
  return hostname !== config.mainDomain && hostname.endsWith(config.baseDomain);
};

/**
 * 메인 도메인 URL 생성
 * 
 * @param path - 경로 (예: '/login', '/terms')
 * @returns string - 전체 URL (예: 'https://mgp.gameking.com/login')
 */
export const getMainDomainUrl = (path: string = ''): string => {
  const protocol = window.location.protocol; // http: or https:
  // Remove any port that may be present in the configured main domain
  const cleanMainDomain = (config.mainDomain || '').replace(/:\d+$/, '');
  return `${protocol}//${cleanMainDomain}${path}`;
};

/**
 * 현재 도메인의 게임 코드 가져오기
 * 
 * @returns string | undefined - 현재 도메인의 gameSvcCd 또는 undefined
 */
export const getCurrentGameCode = (): string | undefined => {
  const hostname = window.location.hostname;
  const domainGameMap = getDomainGameMap();
  return domainGameMap[hostname];
};

/**
 * 현재 도메인의 게임 정보 가져오기
 * 
 * @returns Game | undefined - 현재 도메인의 게임 정보
 */
export const getCurrentGameInfo = () => {
  const gameCode = getCurrentGameCode();
  if (!gameCode) return undefined;
  return gamesWithDomain.find(g => g.code === gameCode);
};

/**
 * 게임별 도메인 URL 생성
 * 
 * @param gameSvcCd - 게임 코드 (예: 'brbr')
 * @param path - 경로 (예: '/shop')
 * @returns string - 전체 URL (예: 'https://bearbrick.gameking.com/shop')
 */
export const getGameDomainUrl = (gameSvcCd: string, path: string = ''): string => {
  const domain = getGameDomain((gameSvcCd || '').toLowerCase());
  if (!domain) {
    console.warn(`Unknown game code: ${gameSvcCd}`);
    return getMainDomainUrl(path);
  }
  const protocol = window.location.protocol;
  // Ensure returned domain does not include a port
  const cleanDomain = (domain || '').replace(/:\d+$/, '');
  return `${protocol}//${cleanDomain}${path}`;
};

/**
 * 모든 게임 정보 가져오기
 * 
 * @returns Array<Game> - 모든 게임 정보
 */
export const getAllGames = () => {
  return gamesWithDomain;
};

/**
 * GTM dataLayer에 이벤트 푸시
 * 
 * @param eventName - 이벤트 이름
 * @param params - 추가 파라미터
 */
export const pushToDataLayer = (eventName: string, params?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    const hostname = window.location.hostname;
    const gameInfo = getCurrentGameInfo();
    
    window.dataLayer.push({
      event: eventName,
      subdomain: hostname.split('.')[0],
      full_domain: hostname,
      game_code: gameInfo?.code,
      game_name: gameInfo?.name,
      ...params
    });
  }
};

// TypeScript 타입 정의
export interface Game {
  code: string;
  name: string;
  fileName: string;
  title: string;
  domain: string;
  ogTitle?: string;
  ogDesc?: string;
  ogImage?: string;
  favicon?: string;
}

// window.dataLayer 타입 선언
declare global {
  interface Window {
    dataLayer: any[];
  }
}
